import { parseJwtPayload } from "~/shared/lib/jwt";
import { toCamelCase } from "~/shared/lib/backend-mapper";
import { env } from "~/env";

const ACCESS_TOKEN_KEY = "auth_access_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";

// Bump this value to force all users to re-login (clears stale tokens).
const AUTH_SCHEMA_VERSION = "3";
const AUTH_SCHEMA_KEY = "auth_schema_version";

if (typeof window !== "undefined" && localStorage.getItem(AUTH_SCHEMA_KEY) !== AUTH_SCHEMA_VERSION) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.setItem(AUTH_SCHEMA_KEY, AUTH_SCHEMA_VERSION);
}

const AUTH_API_URL = `${env.VITE_API_URL}/v2/auth`;

interface TokenPair {
    accessToken: string;
    refreshToken: string;
    tokenType: string;
    expiresIn: number;
}

export function setTokens(tokens: TokenPair): void {
    if (typeof window === "undefined") return;
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    // Копии токена в cookie здесь нет намеренно: backend её не читает (аутентификация
    // только Bearer / X-Api-Key), а cookie без HttpOnly доступна любому скрипту страницы —
    // это была поверхность атаки без единого потребителя.
}

export function getAccessToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens(): void {
    if (typeof window === "undefined") return;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Гасим cookie прежних версий: у уже открытых сессий она осталась в браузере, и
    // разлогин обязан убрать её вместе с токенами, иначе копия переживёт выход.
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${ACCESS_TOKEN_KEY}=; path=/; max-age=0; SameSite=Lax${secure}`;
}

// Разбор payload живёт в `shared/lib/jwt` (одно объявление на фронт); здесь — реэкспорт,
// потому что потребители исторически берут его из модуля аутентификации.
export { parseJwtPayload };

export function isTokenExpired(token: string): boolean {
    const payload = parseJwtPayload(token);
    if (!payload?.exp) return true;
    const expMs = (payload.exp as number) * 1000;
    return Date.now() > expMs - 60_000; // 60s buffer
}

let refreshInFlight: Promise<TokenPair | null> | null = null;

export async function refreshTokens(): Promise<TokenPair | null> {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return null;

        try {
            const response = await fetch(`${AUTH_API_URL}/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
                // Only clear if the refresh token we sent is still the one in
                // localStorage — a parallel refresh may have already succeeded
                // and rotated the token, and we must not wipe the fresh pair.
                if (
                    (response.status === 401 || response.status === 403) &&
                    getRefreshToken() === refreshToken
                ) {
                    clearTokens();
                }
                return null;
            }

            const data = toCamelCase<TokenPair>(await response.json());
            setTokens(data);
            return data;
        } catch {
            return null;
        }
    })();

    try {
        return await refreshInFlight;
    } finally {
        refreshInFlight = null;
    }
}

export async function getValidAccessToken(): Promise<string | null> {
    const token = getAccessToken();
    if (!token) return null;

    if (!isTokenExpired(token)) return token;

    const newTokens = await refreshTokens();
    return newTokens?.accessToken ?? null;
}

export async function requestMagicLink(
    email: string,
): Promise<{ success: boolean; message?: string; status?: number; code?: string }> {
    try {
        const response = await fetch(`${AUTH_API_URL}/magic-link`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            // ml-backend error shape is { message, code }; keep `detail` as a fallback for FastAPI defaults.
            const error = (await response.json().catch(() => ({}))) as {
                message?: string;
                code?: string;
                detail?: string;
            };
            return {
                success: false,
                status: response.status,
                code: error.code,
                message: error.message ?? error.detail ?? "Failed to send magic link",
            };
        }

        return { success: true, status: response.status };
    } catch {
        return { success: false, message: "Network error" };
    }
}

/**
 * Почему запрос магической ссылки не удался. `network` — до сервера не дошли (обрыв,
 * CORS, таймаут); `rejected` — сервер ответил отказом (ссылка просрочена, токен чужой).
 * Различие нужно UI: в первом случае человеку предлагают повторить, во втором —
 * запросить новую ссылку. Раньше стороны договаривались об этом строкой сообщения.
 */
export type MagicLinkFailure = "network" | "rejected";

export interface MagicLinkResult {
    success: boolean;
    failure?: MagicLinkFailure;
    message?: string;
    isFirstLogin?: boolean;
}

export async function verifyMagicLink(identifier: string, token: string): Promise<MagicLinkResult> {
    try {
        const response = await fetch(`${AUTH_API_URL}/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier, token }),
        });

        if (!response.ok) {
            // `.catch` обязателен: не-JSON тело отказа (HTML 502 от прокси, ответ WAF)
            // иначе бросает из `json()`, попадает во внешний catch и превращает отказ
            // сервера в `failure: "network"` — человеку предлагают «повторить» вместо
            // «запросите новую ссылку», ровно то различие, ради которого союз и заведён.
            const error = (await response.json().catch(() => ({}))) as { detail?: string };
            return {
                success: false,
                failure: "rejected",
                message: error.detail ?? "Invalid or expired link",
            };
        }

        const data = toCamelCase<TokenPair & { isFirstLogin?: boolean }>(await response.json());
        setTokens(data);
        return { success: true, isFirstLogin: data.isFirstLogin ?? false };
    } catch {
        return { success: false, failure: "network", message: "Network error" };
    }
}

export async function logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
        try {
            await fetch(`${AUTH_API_URL}/logout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
        } catch {
            // Ignore — clearing local state is enough
        }
    }
    clearTokens();
}
